import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AccessibilitySettings } from '../../models/accessibility-settings.model';
import { ButtonComponent } from "../button/button.component";

@Component({
  selector: 'app-accessibility-menu',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonComponent],
  templateUrl: './accessibility-menu.component.html',
})
export class AccessibilityMenuComponent {
  private readonly defaultSettings: AccessibilitySettings = {
    fontScale: 100,
    highContrast: false,
    grayscale: false,
    underlineLinks: false,
    readingGuide: false,
    bigCursor: false,
    textSpacing: 'normal',
    lineSpacing: 'normal',
    fontWeight: 'normal',
  };
  private _settings: AccessibilitySettings = { ...this.defaultSettings };

  @Input({ required: true })
  set settings(value: AccessibilitySettings) {
    if (!value) {
      this._settings = { ...this.defaultSettings };
      return;
    }
    this._settings = value;
  }
  get settings(): AccessibilitySettings {
    return this._settings;
  }
  @Output() increaseFont = new EventEmitter<void>();
  @Output() decreaseFont = new EventEmitter<void>();
  @Output() toggleHighContrast = new EventEmitter<void>();
  @Output() toggleGrayscale = new EventEmitter<void>();
  @Output() toggleUnderlineLinks = new EventEmitter<void>();
  @Output() toggleReadingGuide = new EventEmitter<void>();
  @Output() toggleBigCursor = new EventEmitter<void>();
  @Output() increaseTextSpacing = new EventEmitter<void>();
  @Output() decreaseTextSpacing = new EventEmitter<void>();
  @Output() increaseLineSpacing = new EventEmitter<void>();
  @Output() decreaseLineSpacing = new EventEmitter<void>();
  @Output() toggleFontWeight = new EventEmitter<void>();
  @Output() startReading = new EventEmitter<void>();
  @Output() stopReading = new EventEmitter<void>();
  @Output() resetAccessibility = new EventEmitter<void>();
  @Input() isSpeechSupported = false;
  @Input() isPlatinumTheme = false;  
  @Input() isReading = false;
  @Input() languageLabel = 'English (EN)';

  menuOpen = false;

  @ViewChild('toggleButton') toggleButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('menuPanel') menuPanel?: ElementRef<HTMLDivElement>;

  getIconPath(icon: string): string {
    const fileName = icon?.split('/').pop() ?? icon;
    return this.isPlatinumTheme ? `/icons/platinum/${fileName}` : `/icons/${icon}`;
  }
  toggleMenu(event: Event) {
    event.stopPropagation();
    this.menuOpen = !this.menuOpen;
    if (this.menuOpen) {
      queueMicrotask(() => {
        const focusable = this.menuPanel?.nativeElement.querySelector<HTMLElement>(
          'button, a, input, [tabindex]:not([tabindex="-1"])'
        );
        focusable?.focus();
      });
    }
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent) {
    if (!this.menuOpen) {
      return;
    }
    const target = event.target as Node;
    if (this.menuPanel?.nativeElement.contains(target) || this.toggleButton?.nativeElement.contains(target)) {
      return;
    }
    this.closeMenu();
  }

  @HostListener('document:keydown.escape')
  handleEscapeKey() {
    if (!this.menuOpen) {
      return;
    }
    this.closeMenu();
  }

  closeMenu() {
    if (!this.menuOpen) {
      return;
    }
    this.menuOpen = false;
    this.toggleButton?.nativeElement.focus();
  }
}
